import { Button, StatusMessage } from "@herefor/design-system";
import { CLAIM_OUTCOME_MESSAGES } from "@herefor/product-language";

import { isRaceLoss, messageFor } from "./claimCopy";
import type { ClaimState } from "./useClaim";

// THE HUMAN MOMENT (bead hf-e6-commitments-coverage-udt.4.2).
//
// Someone tapped "I can help" and somebody else got there first. This is the panel
// they see, and the whole bead is about what it must NOT be: an error.
//
// WHY `tone="success"` FOR A REFUSED CLAIM. It looks wrong until you ask what the
// message is about. It is not about their tap failing — it is about the need being
// covered, which is the outcome everybody wanted (COV-006). `tone="error"` would set
// `role="alert"`, interrupting a screen-reader user mid-sentence, and paint the panel
// in the colour the product uses for things that went wrong. A person who did a
// generous thing would be told, in red, that they had made a mistake.
//
// The other refusals keep `tone="info"`: "this changed since you looked" is neither
// good news nor bad, and dressing it as either would overstate it.
//
// ANNOUNCEMENT IS POLITE, NEVER ASSERTIVE. `StatusMessage` gives `role="status"` and
// `aria-live="polite"`, so the outcome is read out after the current phrase rather
// than cutting across it. Nothing here is urgent; the plan is fine.
//
// NO APOLOGY, NO SECOND-PLACE FRAMING, NO "TRY AGAIN". Every one of those would tell
// the person something went wrong. The action offered is to look at the plan, and —
// where there is one — somewhere else their offer can go.

export interface AdjacentNeed {
  readonly supportNeedId: string;
  /** What the help is, in the plan's own words. */
  readonly label: string;
  /** Already formatted for display by the caller — this component composes no copy. */
  readonly when: string;
}

export interface ClaimOutcomeProps {
  readonly state: ClaimState;
  /** Take the person to the plan they are now looking at a stale version of. */
  readonly onViewPlan?: () => void;
  /**
   * Other open needs, offered after a race loss.
   *
   * The bead calls this a nice-to-have, and it is the difference between "no" and
   * "not this one — but Thursday still needs someone". Omit it and the section simply
   * does not render; pass an empty array to say explicitly that nothing else is open,
   * which is a different and equally warm thing to tell somebody.
   */
  readonly stillNeeded?: readonly AdjacentNeed[];
}

export function ClaimOutcome({ state, onViewPlan, stillNeeded }: ClaimOutcomeProps) {
  if (state.status === "idle" || state.status === "submitting") return null;

  if (state.status === "accepted") {
    return (
      <div className="claim-outcome" data-claim-outcome="accepted">
        <StatusMessage tone="success">
          {/* One child, deliberately: the design system lays a status message out as a
              row, so several paragraphs would become several narrow columns. */}
          <div className="claim-outcome__lines">
            <p className="claim-outcome__title">{CLAIM_OUTCOME_MESSAGES.accepted}</p>
            <p className="claim-outcome__body">
              {CLAIM_OUTCOME_MESSAGES.nowCovered(
                state.value.confirmedCapacity,
                state.value.requiredCapacity,
              )}
            </p>
          </div>
        </StatusMessage>
      </div>
    );
  }

  const raceLoss = isRaceLoss(state.rejection);
  const message = messageFor(state.rejection);

  return (
    <div
      className="claim-outcome"
      data-claim-outcome={raceLoss ? "just-covered" : "refused"}
      data-claim-rejection={state.rejection.kind}
    >
      <StatusMessage tone={raceLoss ? "success" : "info"}>
        <div className="claim-outcome__lines">
          <p className="claim-outcome__title">{message.title}</p>
          <p className="claim-outcome__body">{message.body}</p>

          {raceLoss ? (
            // The truth about the need, from the rejection itself. The guard returns
            // both numbers precisely so this line can be written without waiting for a
            // refetch — the person sees WHY, not just that they were refused.
            <p className="claim-outcome__coverage" data-claim-coverage>
              {CLAIM_OUTCOME_MESSAGES.nowCovered(
                state.rejection.confirmedCapacity,
                state.rejection.requiredCapacity,
              )}
            </p>
          ) : null}

          {onViewPlan === undefined ? null : (
            <p className="claim-outcome__action">
              <Button onClick={onViewPlan} variant="secondary">
                {message.action}
              </Button>
            </p>
          )}
        </div>
      </StatusMessage>

      {raceLoss && stillNeeded !== undefined ? <StillNeeded needs={stillNeeded} /> : null}
    </div>
  );
}

/**
 * Somewhere else the offer can go.
 *
 * Presented as information, never as a prompt: no count, no "you could still help
 * with 3 things", nothing that turns an offer into an obligation. The empty case is
 * warm and final rather than a dead end.
 */
function StillNeeded({ needs }: { readonly needs: readonly AdjacentNeed[] }) {
  if (needs.length === 0) {
    return (
      <p className="claim-outcome__nothing-else" data-claim-still-needed="none">
        {CLAIM_OUTCOME_MESSAGES.nothingElseNeeded}
      </p>
    );
  }

  return (
    <section className="claim-outcome__still-needed" data-claim-still-needed="some">
      <h3 className="claim-outcome__still-needed-title">
        {CLAIM_OUTCOME_MESSAGES.stillNeededTitle}
      </h3>
      <ul className="claim-outcome__needs">
        {needs.map((need) => (
          <li key={need.supportNeedId}>
            <span className="claim-outcome__need-label">{need.label}</span>
            <span className="claim-outcome__need-when">{need.when}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
